import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";

const MerchantTable = () => {
    const { callApi: hierarchy } = useApi("/hierarchy/root", "GET");
    const { callApi: transferApi } = useApi("/p2p/transfer", "POST");
    const { callApi: reversalApiCall } = useApi("/p2p/reversal", "POST");

    const mobile_number = useSelector(
        (state) => state.auth.employeeDetails?.mobile_number
    );

    const mid = useSelector(
        (state) => state.auth.employeeDetails?.username
    );

    const [merchants, setMerchants] = useState([]);
    const [loading, setLoading] = useState(false);

    const [selectedRow, setSelectedRow] = useState(null);
    const [direction, setDirection] = useState("forward");

    const [rowAmounts, setRowAmounts] = useState({});
    const [rowMessage, setRowMessage] = useState({});
    const [rowLoading, setRowLoading] = useState(null);
    const [mpinModal, setMpinModal] = useState({ show: false, mpin: "", pendingMid: null, pendingPayload: null, type: null });
    const mpinRefs = useRef([]);

    /* ✅ NEW SORT STATE */
    const [sortType, setSortType] = useState("nameAsc");

    /* ================= FETCH ================= */
    const fetchMerchants = async () => {
        setLoading(true);
        try {
            const res = await hierarchy(null, `?mobile_number=${mobile_number}`);
            const merchantList =
                res?.self?.children?.filter((c) => c.role === "MERCHANT") || [];
            setMerchants(merchantList);
        } catch {
            setMerchants([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (mobile_number) fetchMerchants();
    }, [mobile_number]);

    /* ✅ SORTED MERCHANTS */
    const sortedMerchants = useMemo(() => {
        const list = [...merchants];

        if (sortType === "nameAsc") {
            return list.sort((a, b) =>
                a.name.trim().toLowerCase()
                    .localeCompare(b.name.trim().toLowerCase())
            );
        }

        if (sortType === "balanceHigh") {
            return list.sort(
                (a, b) =>
                    (b?.balances?.mainBalance || 0) -
                    (a?.balances?.mainBalance || 0)
            );
        }

        if (sortType === "balanceLow") {
            return list.sort(
                (a, b) =>
                    (a?.balances?.mainBalance || 0) -
                    (b?.balances?.mainBalance || 0)
            );
        }

        return list;
    }, [merchants, sortType]);

    /* ================= CHECKBOX ================= */
    const handleCheckbox = (merchantMid) => {
        if (selectedRow === merchantMid) {
            setSelectedRow(null);
        } else {
            setSelectedRow(merchantMid);
            setDirection("forward");
        }
    };

    /* ================= AMOUNT ================= */
    const handleAmountChange = (mid, value) => {
        setRowAmounts((prev) => ({
            ...prev,
            [mid]: value,
        }));
    };

    /* ================= SEND ================= */
    const handleSend = (merchantMid) => {
        const amount = Number(rowAmounts[merchantMid]);

        if (!amount || amount <= 0) {
            setRowMessage({ [merchantMid]: { type: "error", text: "Please enter valid amount" } });
            return;
        }

        const payload = direction === "forward"
            ? { senderMid: mid, receiverMid: merchantMid, amount, currency: "INR", walletType: "MRCHNT", remarks: "P2P Send" }
            : { requesterMid: mid, targetMid: merchantMid, amount, currency: "INR", walletType: "MRCHNT", remarks: "Distributor balance adjustment" };

        setMpinModal({
            show: true, mpin: "",
            pendingMid: merchantMid,
            pendingPayload: payload,
            type: direction === "forward" ? "transfer" : "reversal",
        });
    };

    const handleMpinConfirm = async () => {
        if (mpinModal.mpin.length !== 6) return;

        const merchantMid = mpinModal.pendingMid;
        setRowLoading(merchantMid);
        setRowMessage({});

        try {
            const apiCall = mpinModal.type === "reversal" ? reversalApiCall : transferApi;
            const res = await apiCall({ ...mpinModal.pendingPayload, mpin: mpinModal.mpin });
            setMpinModal({ show: false, mpin: "", pendingMid: null, pendingPayload: null, type: null });

            if (res?.status?.toLowerCase() === "success") {
                setRowMessage({ [merchantMid]: { type: "success", text: "Transaction Successful" } });
                setSelectedRow(null);
                setDirection("forward");
                setRowAmounts((prev) => ({ ...prev, [merchantMid]: "" }));
                setTimeout(() => { setRowMessage({}); }, 3000);
            } else {
                setRowMessage({ [merchantMid]: { type: "error", text: res?.message || "Operation Failed" } });
            }
        } catch (err) {
            setMpinModal({ show: false, mpin: "", pendingMid: null, pendingPayload: null, type: null });
            setRowMessage({
                [merchantMid]: {
                    type: "error",
                    text: err?.response?.data?.message || err?.message || "Operation failed",
                },
            });
        } finally {
            setRowLoading(null);
        }
    };

    return (
        <div className="p-6">

            {/* ✅ HEADER WITH SORT (Responsive) */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">

                {/* Title */}
                <h2 className="text-xl font-bold text-gray-800">
                    Merchant List
                </h2>

                {/* Sort Section */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">

                    <span className="text-sm font-medium text-gray-600">
                        Sort By:
                    </span>

                    <select
                        value={sortType}
                        onChange={(e) => setSortType(e.target.value)}
                        className="w-full sm:w-auto border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
                    >
                        <option value="nameAsc">Alphabetical (A-Z)</option>
                        <option value="balanceHigh">Balance High → Low</option>
                        <option value="balanceLow">Balance Low → High</option>
                    </select>

                </div>
            </div>
            {/* ================= DESKTOP TABLE ================= */}
            <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-300 shadow-sm">
                <table className="min-w-full text-sm border-collapse">

                    <thead className="bg-[#0A2A4F] text-white">
                        <tr>
                            {[
                                "Select",
                                "Sr No",
                                "Shop Name",
                                "Mobile",
                                "Balance",
                                "Amount",
                                "Direction",
                                "Action"
                            ].map((head) => (
                                <th
                                    key={head}
                                    className="px-4 py-3 text-left font-semibold border border-[#34557a]"
                                >
                                    {head}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="8" className="text-center py-8 border">
                                    Loading...
                                </td>
                            </tr>
                        ) : sortedMerchants.map((m, index) => {
                            const isActive = selectedRow === m.mid;
                            const message = rowMessage[m.mid];

                            return (
                                <React.Fragment key={m.userId || index}>
                                    <tr className="transition duration-150 hover:bg-gray-50">
                                        <td className="border px-4 py-3 text-center">
                                            <input
                                                type="checkbox"
                                                checked={isActive}
                                                onChange={() => handleCheckbox(m.mid)}
                                            />
                                        </td>

                                        <td className="border px-4 py-3">{index + 1}</td>
                                        <td className="border px-4 py-3 font-medium">{m.name}</td>
                                        <td className="border px-4 py-3">{m.mobile}</td>

                                        <td className="border px-4 py-3 text-right font-semibold text-green-600">
                                            ₹ {Number(m?.balances?.mainBalance || 0).toLocaleString("en-IN", {
                                                minimumFractionDigits: 2,
                                            })}
                                        </td>

                                        <td className="border px-4 py-3">
                                            <input
                                                type="number"
                                                disabled={!isActive}
                                                value={rowAmounts[m.mid] || ""}
                                                onChange={(e) =>
                                                    handleAmountChange(m.mid, e.target.value)
                                                }
                                                className="w-full rounded-md px-3 py-1.5 text-sm border border-blue-300 focus:ring-2 focus:ring-blue-300 focus:outline-none"
                                                placeholder="Enter amount"
                                            />
                                        </td>

                                        <td className="border px-4 py-3 space-x-3 text-sm">
                                            <label>
                                                <input
                                                    type="radio"
                                                    disabled={!isActive}
                                                    checked={isActive && direction === "forward"}
                                                    onChange={() => setDirection("forward")}
                                                /> Forward
                                            </label>

                                            <label>
                                                <input
                                                    type="radio"
                                                    disabled={!isActive}
                                                    checked={isActive && direction === "reverse"}
                                                    onChange={() => setDirection("reverse")}
                                                /> Reverse
                                            </label>
                                        </td>

                                        <td className="border px-4 py-3 text-center">
                                            <button
                                                disabled={!isActive || rowLoading === m.mid}
                                                onClick={() => handleSend(m.mid)}
                                                className={`px-4 py-1.5 rounded-md text-xs font-medium transition
                                        ${isActive
                                                        ? "bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
                                                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                                    }`}
                                            >
                                                {rowLoading === m.mid ? "Processing..." : "Send"}
                                            </button>
                                        </td>
                                    </tr>

                                    {message && (
                                        <tr>
                                            <td colSpan="8" className="border px-4 py-2">
                                                <div
                                                    className={`rounded-md px-3 py-2 text-sm font-medium
                                            ${message.type === "success"
                                                            ? "bg-green-100 text-green-700"
                                                            : "bg-red-100 text-red-700"
                                                        }`}
                                                >
                                                    {message.text}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>


            {/* ================= MOBILE CARD VIEW ================= */}
            <div className="block md:hidden space-y-4">
                {loading ? (
                    <div className="text-center py-8">Loading...</div>
                ) : sortedMerchants.map((m, index) => {
                    const isActive = selectedRow === m.mid;
                    const message = rowMessage[m.mid];

                    return (
                        <div
                            key={m.userId || index}
                            className="border rounded-xl p-4 shadow-sm bg-white"
                        >
                            <div className="flex justify-between items-center mb-2">
                                <div className="font-semibold text-gray-800">
                                    {m.name}
                                </div>
                                <input
                                    type="checkbox"
                                    checked={isActive}
                                    onChange={() => handleCheckbox(m.mid)}
                                />
                            </div>

                            <div className="text-sm text-gray-600 mb-1">
                                Mobile: {m.mobile}
                            </div>

                            <div className="text-green-600 font-semibold mb-3">
                                ₹ {Number(m?.balances?.mainBalance || 0).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                })}
                            </div>

                            <input
                                type="number"
                                disabled={!isActive}
                                value={rowAmounts[m.mid] || ""}
                                onChange={(e) =>
                                    handleAmountChange(m.mid, e.target.value)
                                }
                                className="w-full rounded-md px-3 py-2 text-sm border border-blue-300 focus:ring-2 focus:ring-blue-300 focus:outline-none mb-3"
                                placeholder="Enter amount"
                            />

                            <div className="flex gap-4 mb-3 text-sm">
                                <label>
                                    <input
                                        type="radio"
                                        disabled={!isActive}
                                        checked={isActive && direction === "forward"}
                                        onChange={() => setDirection("forward")}
                                    /> Forward
                                </label>

                                <label>
                                    <input
                                        type="radio"
                                        disabled={!isActive}
                                        checked={isActive && direction === "reverse"}
                                        onChange={() => setDirection("reverse")}
                                    /> Reverse
                                </label>
                            </div>

                            <button
                                disabled={!isActive || rowLoading === m.mid}
                                onClick={() => handleSend(m.mid)}
                                className={`w-full py-2 rounded-md text-sm font-medium transition
                        ${isActive
                                        ? "bg-blue-600 text-white"
                                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    }`}
                            >
                                {rowLoading === m.mid ? "Processing..." : "Send"}
                            </button>

                            {message && (
                                <div
                                    className={`mt-3 rounded-md px-3 py-2 text-sm font-medium
                            ${message.type === "success"
                                            ? "bg-green-100 text-green-700"
                                            : "bg-red-100 text-red-700"
                                        }`}
                                >
                                    {message.text}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {mpinModal.show && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[200] px-4">
                    <div className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl space-y-5">
                        <h2 className="text-xl font-bold text-gray-800 text-center">Enter MPIN</h2>
                        <p className="text-sm text-gray-500 text-center">
                            Enter your 6-digit MPIN to confirm the transaction.
                        </p>
                        <div className="flex justify-center gap-2">
                            {[...Array(6)].map((_, i) => (
                                <input
                                    key={i}
                                    ref={(el) => (mpinRefs.current[i] = el)}
                                    type="password"
                                    maxLength={1}
                                    value={mpinModal.mpin[i] || ""}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, "");
                                        const arr = mpinModal.mpin.split("");
                                        arr[i] = val;
                                        setMpinModal(prev => ({ ...prev, mpin: arr.join("") }));
                                        if (val && i < 5) mpinRefs.current[i + 1]?.focus();
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Backspace" && !mpinModal.mpin[i] && i > 0)
                                            mpinRefs.current[i - 1]?.focus();
                                    }}
                                    className="w-11 h-11 text-center text-xl border-2 border-slate-300 rounded-lg focus:border-indigo-600 outline-none"
                                />
                            ))}
                        </div>
                        <div className="flex gap-3 pt-1">
                            <button
                                onClick={() => { setMpinModal({ show: false, mpin: "", pendingMid: null, pendingPayload: null, type: null }); mpinRefs.current = []; }}
                                className="flex-1 border-2 border-gray-300 text-gray-600 py-3 rounded-2xl font-semibold"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleMpinConfirm}
                                disabled={mpinModal.mpin.length !== 6 || rowLoading !== null}
                                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-2xl font-semibold disabled:opacity-50"
                            >
                                {rowLoading !== null ? "Processing..." : "Confirm"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MerchantTable;